import { useMemo, useState } from "react";
import {
  AI_DAILY_ALERT_BUDGET_USD,
  CHAT_MODEL_RESERVE_USD,
  EVALUATE_JOB_MODEL_DAILY_BUDGET_USD,
  parseTokenUsageMaps,
  resolveMonthCost,
  utcMonthKey,
  type AdminTokenSummary,
  type AiOperation,
  type ModelCostEntry,
  type OperationStat,
} from "@winche/shared";
import { Badge, Callout } from "@winche/ui";
import {
  Block,
  EmptyState,
  ErrorMessage,
  Freshness,
  LoadButton,
  LoadingBlock,
  Metric,
  MetricGrid,
  Panel,
  SectionHeader,
  Segmented,
  SourceNote,
} from "../components/ui";
import type { AdminFailure } from "../lib/callable-error";
import { BarList, Chart, type ChartDatum } from "../components/chart";
import { DataTable, type Column } from "../components/table";
import { compactNum, monthLabel, num, pct, shortDay, toMillis, usd, usdCompact, usdUnit } from "../lib/format";
import type { UserTokenStat } from "./UsersSection";
import type { AdminModelDailyBudget } from "./OverviewSection";

interface Props {
  summary: AdminTokenSummary | null;
  freeForAll: boolean | null;
  users: UserTokenStat[] | null;
  loadingUsers: boolean;
  usersError: AdminFailure | null;
  onLoadUsers: () => void;
  /** `_admin/aiDailySeries.series`: `YYYY-MM-DD` → USD cost of every model (last 30 days). */
  dailySeries: Record<string, number> | null;
  /** `_admin/aiDailySeries.anthropicSeries`: the Haiku cap's counter per day (`_evaluateJobDaily.modelCostUsd`). */
  anthropicDailySeries?: Record<string, number> | null;
  /**
   * `_admin/aiDailySeries.otherSeries`: per day, the cost of the models that
   * are not Anthropic's (Flash Lite, a fallback, Jev), what the alert reads.
   */
  otherDailySeries?: Record<string, number> | null;
  /** `config/global.aiDailyBudgetUsd`: the alert line of the models other than Anthropic's (it only notifies). */
  dailyBudgetUsd: number | null;
  /** `_admin/modelDailyBudget`: the Haiku cap and chat reserve in force (defaults otherwise). */
  modelBudget?: AdminModelDailyBudget | null;
  listenerFailed?: boolean;
}

type SortMode = "cost" | "calls" | "tokens";

/** Days the daily chart and its counts cover: what aiBudgetGuard publishes. */
const DAILY_SERIES_DAYS = 30;

/** Decode Firestore model key (`google_gemini-2.5-flash-lite_free`) → readable id. */
function decodeModelKey(key: string): { id: string; isFree: boolean } {
  const isFree = key.endsWith("_free");
  const base = isFree ? key.slice(0, -5) : key;
  const readable = base.replace(/^([^_]+)_/, "$1/");
  return { id: isFree ? `${readable}:free` : readable, isFree };
}

/** Share of `part` in `whole` as "42%", or "—". */
function share(part: number, whole: number): string {
  return whole > 0 ? pct((part / whole) * 100, 0) : "—";
}

export function AiSpendSection({
  summary,
  freeForAll,
  users,
  loadingUsers,
  usersError,
  onLoadUsers,
  dailySeries,
  anthropicDailySeries,
  otherDailySeries,
  dailyBudgetUsd,
  modelBudget,
  listenerFailed = false,
}: Props) {
  const [modelSort, setModelSort] = useState<SortMode>("cost");
  const monthKey = utcMonthKey();
  const parsed = useMemo(
    () => (summary ? parseTokenUsageMaps(summary as unknown as Record<string, unknown>) : null),
    [summary],
  );

  const monthCost = resolveMonthCost(parsed?.monthlyAiCost, monthKey);
  const lifetime = summary?.totalEstimatedCostUsd ?? 0;
  const dayOfMonth = new Date().getUTCDate();
  const avgPerDay = monthCost.value != null && dayOfMonth > 0 ? monthCost.value / dayOfMonth : null;
  // Two different lines (ux-admin-04, q-fn-scheduled-05): the alert of the models other than
  // Anthropic's only notifies; the Haiku cap stops background work.
  const budget = dailyBudgetUsd ?? AI_DAILY_ALERT_BUDGET_USD;
  const haikuCap = modelBudget?.budgetUsd ?? EVALUATE_JOB_MODEL_DAILY_BUDGET_USD;
  const backgroundLimit = modelBudget?.backgroundLimitUsd ?? Math.max(0, haikuCap - (modelBudget?.chatReserveUsd ?? CHAT_MODEL_RESERVE_USD));

  const daily: ChartDatum[] = useMemo(() => {
    if (!dailySeries && !anthropicDailySeries) return [];
    const keys = new Set([...Object.keys(dailySeries ?? {}), ...Object.keys(anthropicDailySeries ?? {})]);
    // The last 30 days only, even if the doc kept older ones (a merge used to leave them).
    return [...keys]
      .sort((a, b) => a.localeCompare(b))
      .slice(-DAILY_SERIES_DAYS)
      .map((key) => ({
        x: key,
        xLabel: shortDay(key),
        values: {
          cost: dailySeries?.[key] ?? null,
          other: otherDailySeries ? (otherDailySeries[key] ?? null) : null,
          anthropic: anthropicDailySeries ? (anthropicDailySeries[key] ?? null) : null,
        },
      }));
  }, [dailySeries, otherDailySeries, anthropicDailySeries]);
  const hasAnthropicSeries = anthropicDailySeries != null && Object.keys(anthropicDailySeries).length > 0;
  const dailyTotal = daily.reduce((s, d) => s + (d.values.cost ?? 0), 0);
  // The alert reads the models other than Anthropic's; until aiBudgetGuard publishes that series, nothing is over it.
  const hasOtherSeries = otherDailySeries != null && Object.keys(otherDailySeries).length > 0;
  const daysOverBudget = hasOtherSeries ? daily.filter((d) => (d.values.other ?? 0) > budget).length : 0;
  const daysPaused = daily.filter((d) => d.values.anthropic != null && d.values.anthropic >= backgroundLimit).length;

  const monthly: ChartDatum[] = useMemo(() => {
    const map = parsed?.monthlyAiCost ?? {};
    return Object.entries(map)
      .filter(([, v]) => Number.isFinite(v))
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => {
        const incomplete = resolveMonthCost(map, key).completeness !== "complete";
        return {
          x: key,
          xLabel: monthLabel(key).replace(/ \d{4}$/, ""),
          tooltipLabel: `${monthLabel(key)}${incomplete ? " (in progress)" : ""}`,
          values: { cost: value },
          muted: incomplete,
        };
      });
  }, [parsed]);

  const modelEntries = useMemo(() => {
    const raw = (parsed?.modelCosts ?? summary?.modelCosts ?? {}) as Record<string, ModelCostEntry | Record<string, number>>;
    return Object.entries(raw)
      .map(([key, entry]) => {
        const e = entry as ModelCostEntry;
        const costUsd = Number(e.costUsd) || 0;
        const callCount = Number(e.callCount) || 0;
        const promptTokens = Number(e.promptTokens) || 0;
        const completionTokens = Number(e.completionTokens) || 0;
        const fallbackCount = Number(e.fallbackCount) || 0;
        const tokens = promptTokens + completionTokens;
        const { id, isFree } = decodeModelKey(key);
        return { key, id, isFree, costUsd, callCount, tokens, fallbackCount };
      })
      .filter((e) => e.callCount > 0 || e.costUsd > 0 || e.tokens > 0);
  }, [parsed, summary]);
  type ModelEntry = (typeof modelEntries)[number];

  const sortedModels = useMemo(() => {
    const copy = [...modelEntries];
    copy.sort((a, b) =>
      modelSort === "cost" ? b.costUsd - a.costUsd : modelSort === "calls" ? b.callCount - a.callCount : b.tokens - a.tokens,
    );
    return copy;
  }, [modelEntries, modelSort]);

  const totalModelCost = modelEntries.reduce((s, e) => s + e.costUsd, 0);
  const totalModelCalls = modelEntries.reduce((s, e) => s + e.callCount, 0);
  const totalModelTokens = modelEntries.reduce((s, e) => s + e.tokens, 0);
  const freeCost = modelEntries.filter((e) => e.isFree).reduce((s, e) => s + e.costUsd, 0);
  const paidCost = totalModelCost - freeCost;
  const freeCalls = modelEntries.filter((e) => e.isFree).reduce((s, e) => s + e.callCount, 0);
  const paidCalls = totalModelCalls - freeCalls;
  const totalFallbacks = modelEntries.reduce((s, e) => s + e.fallbackCount, 0);

  const opEntries = useMemo(() => {
    const stats = (parsed?.operationStats ?? summary?.operationStats ?? {}) as Partial<
      Record<AiOperation, OperationStat | Record<string, number>>
    >;
    const counts = (parsed?.operationCounts ?? summary?.operationCounts ?? {}) as Partial<Record<AiOperation, number>>;
    const ops = new Set([...Object.keys(stats), ...Object.keys(counts)]) as Set<AiOperation>;
    return [...ops]
      .map((op) => {
        const st = (stats[op] ?? {}) as OperationStat;
        const tokens = (Number(st.promptTokens) || 0) + (Number(st.completionTokens) || 0);
        const callCount = Number(st.callCount) || Number(counts[op]) || 0;
        return { op, tokens, callCount };
      })
      .filter((e) => e.callCount > 0 || e.tokens > 0)
      .sort((a, b) => b.tokens - a.tokens || b.callCount - a.callCount);
  }, [parsed, summary]);
  const totalOpTokens = opEntries.reduce((s, e) => s + e.tokens, 0);

  const topModel = [...modelEntries].sort((a, b) => b.costUsd - a.costUsd)[0];
  const topOp = opEntries[0];
  const hasSummary = summary != null;

  const topUsers = useMemo(() => {
    if (!users) return [];
    return [...users].sort((a, b) => b.totalEstimatedCostUsd - a.totalEstimatedCostUsd).slice(0, 10);
  }, [users]);

  const modelColumns: Column<ModelEntry>[] = [
    {
      key: "model",
      header: "Model",
      cell: (e) => (
        <span className="flex items-center gap-1.5">
          <span className="font-mono">{e.id}</span>
          {e.isFree && (
            <Badge tone="success" size="sm">
              free
            </Badge>
          )}
        </span>
      ),
      sortValue: (e) => e.id,
      footer: "Total",
    },
    { key: "cost", header: "Cost", align: "right", cell: (e) => usd(e.costUsd), sortValue: (e) => e.costUsd, footer: usd(totalModelCost) },
    {
      key: "share",
      header: "Share",
      align: "right",
      cell: (e) => <span className="text-muted-foreground">{share(e.costUsd, totalModelCost)}</span>,
      sortValue: (e) => e.costUsd,
    },
    { key: "calls", header: "Calls", align: "right", cell: (e) => num(e.callCount), sortValue: (e) => e.callCount, footer: num(totalModelCalls) },
    {
      key: "fallbacks",
      header: "Fallbacks",
      align: "right",
      title: "Free-tier calls that fell back to the paid model",
      cell: (e) =>
        e.isFree ? (
          <span className={e.fallbackCount > 0 ? "text-danger-fg" : undefined}>{num(e.fallbackCount)}</span>
        ) : (
          <span className="text-subtle-foreground">—</span>
        ),
      sortValue: (e) => e.fallbackCount,
      footer: <span className={totalFallbacks > 0 ? "text-warning-fg" : undefined}>{num(totalFallbacks)}</span>,
    },
    { key: "tokens", header: "Tokens", align: "right", cell: (e) => compactNum(e.tokens), sortValue: (e) => e.tokens, footer: compactNum(totalModelTokens) },
    {
      key: "avg",
      header: "Avg / call",
      align: "right",
      cell: (e) => <span className="text-muted-foreground">{usdUnit(e.callCount > 0 ? e.costUsd / e.callCount : null)}</span>,
      sortValue: (e) => (e.callCount > 0 ? e.costUsd / e.callCount : null),
    },
  ];

  const userColumns: Column<UserTokenStat>[] = [
    { key: "rank", header: "#", cell: (_u, i) => <span className="text-muted-foreground tabular-nums">{i + 1}</span> },
    {
      key: "user",
      header: "User",
      cell: (u) => (
        <span className="flex items-center gap-1.5">
          {u.displayName || u.uid.slice(0, 8)}
          {u.isDeleted && (
            <Badge tone="neutral" size="sm">
              deleted
            </Badge>
          )}
        </span>
      ),
    },
    { key: "cost", header: "Cost", align: "right", cell: (u) => usdUnit(u.totalEstimatedCostUsd) },
    { key: "tokens", header: "Tokens", align: "right", cell: (u) => compactNum(u.totalTokens) },
    {
      key: "share",
      header: "Share of lifetime",
      align: "right",
      cell: (u) => <span className="text-muted-foreground">{lifetime > 0 ? pct((u.totalEstimatedCostUsd / lifetime) * 100) : "—"}</span>,
    },
  ];

  return (
    <div>
      <SectionHeader
        title="AI Spend"
        subtitle="Where OpenRouter dollars and tokens go."
        meta={<Freshness at={toMillis(summary?.lastUpdatedAt)} failed={listenerFailed} source="_admin/tokenUsageSummary" />}
      />

      {freeForAll && (
        <Callout tone="warning" className="mb-4" title="Free-for-all is on">
          Plan limits are off for every user. The Haiku daily cap still stops background work: past it, more usage waits for
          the next UTC day.
        </Callout>
      )}

      {!hasSummary && <EmptyState message="No AI usage summary yet. Stats appear after the first token_usage write." />}

      {hasSummary && (
        <>
          <MetricGrid cols={4} className="mb-6">
            <Metric
              label={`This month · ${monthKey}`}
              value={usd(monthCost.value)}
              sub={monthCost.completeness !== "complete" ? "Month in progress" : undefined}
              hint="Observed cost for the UTC month. A missing month is —, never $0."
            />
            <Metric label="Average per day" value={usd(avgPerDay)} sub="This UTC month" />
            <Metric label="Lifetime AI cost" value={usd(lifetime)} />
            <Metric
              label="Total tokens"
              value={compactNum(summary.totalTokens)}
              sub={`${compactNum(summary.totalPromptTokens)} prompt · ${compactNum(summary.totalCompletionTokens)} completion`}
            />
          </MetricGrid>

          {(topModel || topOp) && (
            <Panel className="mb-8 space-y-1 text-label">
              {topModel && totalModelCost > 0 ? (
                <p>
                  Top model <span className="font-mono">{topModel.id}</span>: {usd(topModel.costUsd)} (
                  {share(topModel.costUsd, totalModelCost)} of model cost){topModel.isFree ? ", tagged free — check the billed cost" : ""}.
                </p>
              ) : null}
              {topOp ? (
                <p>
                  Heaviest operation <span className="font-mono">{topOp.op}</span>: {compactNum(topOp.tokens)} tokens (
                  {share(topOp.tokens, totalOpTokens)}).
                </p>
              ) : null}
              {totalModelCost > 0 ? (
                <p className="text-muted-foreground">
                  Paid models {usd(paidCost)} ({share(paidCost, totalModelCost)}) · free-tagged {usd(freeCost)} (
                  {share(freeCost, totalModelCost)})
                  {totalFallbacks > 0 ? <span className="text-warning-fg"> · {num(totalFallbacks)} free→paid fallbacks</span> : null}
                </p>
              ) : null}
            </Panel>
          )}

          <Block
            title="Daily cost · last 30 days (UTC)"
            hint="Bars: every model; the second bar is the models other than Anthropic's (Flash Lite, fallback, Jev), the spend the alert (aiDailyBudgetUsd) watches. Line: Anthropic spend, the Haiku cap's counter: the cap stops background work at the cap minus the chat reserve, and Anthropic spend alerts 10 % past the cap."
          >
            <Panel>
              {daily.length > 0 ? (
                <>
                  <Chart
                    title="Daily AI cost, last 30 days"
                    data={daily}
                    series={[
                      { key: "cost", label: "AI cost", color: "var(--chart-1)", kind: "bar" },
                      ...(hasOtherSeries ? [{ key: "other", label: "Other models", color: "var(--chart-3)", kind: "bar" as const }] : []),
                      ...(hasAnthropicSeries ? [{ key: "anthropic", label: "Anthropic (Haiku)", color: "var(--chart-2)", kind: "line" as const }] : []),
                    ]}
                    format={(v) => usdUnit(v)}
                    formatAxis={usdCompact}
                    reference={[
                      { value: budget, label: `Other-models alert ${usd(budget)}`, tone: "warning" },
                      { value: haikuCap, label: `Haiku cap ${usd(haikuCap)}`, tone: "danger" },
                    ]}
                    legend
                    showLastValue={false}
                  />
                  <SourceNote>
                    {daily.length} days: {usd(dailyTotal)} · {usdUnit(dailyTotal / daily.length)} per day
                    {daysOverBudget > 0 ? ` · ${daysOverBudget} over the alert` : ""}
                    {hasAnthropicSeries && daysPaused > 0 ? ` · ${daysPaused} with background paused (Anthropic ≥ ${usd(backgroundLimit)})` : ""} ·
                    Source: _admin/aiDailySeries, hourly
                  </SourceNote>
                </>
              ) : (
                <EmptyState message="No daily series yet. aiBudgetGuard publishes it on its next hourly run." />
              )}
            </Panel>
          </Block>

          <Block title="Monthly cost (UTC)">
            <Panel>
              {monthly.length > 0 ? (
                <>
                  <Chart
                    title="Monthly AI cost"
                    data={monthly}
                    series={[{ key: "cost", label: "AI cost", color: "var(--chart-1)", kind: "bar" }]}
                    format={usd}
                    formatAxis={usdCompact}
                    showLastValue={false}
                  />
                  <SourceNote>The current month is drawn lighter until it closes.</SourceNote>
                </>
              ) : (
                <EmptyState message="No monthlyAiCost entries yet." />
              )}
            </Panel>
          </Block>

          <div className="mb-8 grid gap-6 @4xl:grid-cols-2">
            <Block title="Paid vs free-tagged" className="mb-0" hint="Free-tagged rows can still show OpenRouter billed cost.">
              <Panel>
                {totalModelCost > 0 ? (
                  <>
                    <div className="flex h-3 overflow-hidden rounded-full bg-muted" role="img" aria-label={`Paid ${share(paidCost, totalModelCost)}, free-tagged ${share(freeCost, totalModelCost)}`}>
                      <div className="h-full bg-[color:var(--chart-1)]" style={{ width: `${(paidCost / totalModelCost) * 100}%` }} />
                      <div className="h-full bg-[color:var(--chart-6)]" style={{ width: `${(freeCost / totalModelCost) * 100}%` }} />
                    </div>
                    <dl className="mt-4 space-y-2 text-label">
                      {[
                        { label: "Paid models", color: "bg-[color:var(--chart-1)]", cost: paidCost, calls: paidCalls },
                        { label: "Free-tagged", color: "bg-[color:var(--chart-6)]", cost: freeCost, calls: freeCalls },
                      ].map((row) => (
                        <div key={row.label} className="flex items-center justify-between gap-3">
                          <dt className="flex items-center gap-2">
                            <span className={`size-2 rounded-sm ${row.color}`} aria-hidden="true" />
                            {row.label}
                          </dt>
                          <dd className="tabular-nums text-muted-foreground">
                            <span className="text-foreground">{usd(row.cost)}</span> · {share(row.cost, totalModelCost)} · {num(row.calls)} calls
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </>
                ) : (
                  <EmptyState message="No cost data yet." />
                )}
              </Panel>
            </Block>

            <Block
              title="Cost by model"
              className="mb-0"
              action={
                <Segmented
                  label="Rank models by"
                  value={modelSort}
                  onChange={setModelSort}
                  options={[
                    { value: "cost", label: "Cost" },
                    { value: "calls", label: "Calls" },
                    { value: "tokens", label: "Tokens" },
                  ]}
                />
              }
            >
              <Panel>
                {sortedModels.length > 0 ? (
                  <BarList
                    rows={sortedModels.map((e) => ({
                      key: e.key,
                      title: e.id,
                      label: (
                        <span className="flex items-center gap-1.5">
                          <span className="truncate font-mono">{e.id}</span>
                          {e.isFree && (
                            <Badge tone="success" size="sm">
                              free
                            </Badge>
                          )}
                        </span>
                      ),
                      value: modelSort === "cost" ? e.costUsd : modelSort === "calls" ? e.callCount : e.tokens,
                      display: modelSort === "cost" ? usdUnit(e.costUsd) : modelSort === "calls" ? num(e.callCount) : compactNum(e.tokens),
                      meta: modelSort === "cost" ? share(e.costUsd, totalModelCost) : undefined,
                    }))}
                    moreLabel={(n) => `${n} more in the table below`}
                  />
                ) : (
                  <EmptyState message="No modelCosts yet." />
                )}
              </Panel>
            </Block>
          </div>

          <Block title="Tokens by operation" hint="Operations store tokens, not USD: token volume is the proxy for spend.">
            <Panel>
              {opEntries.length > 0 ? (
                <BarList
                  max={10}
                  rows={opEntries.map((e) => ({
                    key: e.op,
                    label: <span className="font-mono">{e.op}</span>,
                    title: e.op,
                    value: e.tokens > 0 ? e.tokens : e.callCount,
                    display: e.tokens > 0 ? compactNum(e.tokens) : `${num(e.callCount)} calls`,
                    meta: e.tokens > 0 ? `${share(e.tokens, totalOpTokens)} · ${num(e.callCount)} calls` : undefined,
                  }))}
                  moreLabel={(n) => `${n} more operations`}
                />
              ) : (
                <EmptyState message="No operationStats yet." />
              )}
            </Panel>
          </Block>

          {modelEntries.length > 0 && (
            <Block title="Model detail" hint="Cost attributed by OpenRouter, lifetime.">
              <DataTable
                caption="Model detail"
                columns={modelColumns}
                rows={modelEntries}
                rowKey={(e) => e.key}
                defaultSort={{ key: "cost", dir: "desc" }}
              />
            </Block>
          )}

          <Block title="Top spenders (lifetime)" action={<LoadButton loading={loadingUsers} loaded={users != null} onLoad={onLoadUsers} />}>
            {usersError && <ErrorMessage failure={usersError} onRetry={onLoadUsers} retrying={loadingUsers} />}
            {users == null && loadingUsers ? (
              <LoadingBlock metrics={0} rows={5} label="Loading users" />
            ) : users == null ? (
              <EmptyState message="Load user stats to see who drives OpenRouter spend." />
            ) : topUsers.length === 0 ? (
              <EmptyState message="No per-user token summaries found." />
            ) : (
              <DataTable caption="Top spenders" columns={userColumns} rows={topUsers} rowKey={(u) => u.uid} />
            )}
          </Block>
        </>
      )}
    </div>
  );
}
